/**
 * /transit — "Where should we start?": upload a transition's documents. Built from the Bridge Figma file's Happy Path, frame "New,
 * Transition, Empty" (243:2256).
 *
 * Uploading opens the New Transition modal (Happy Path frames 2–4: the
 * documents scanned, listed by type, then name and details, and Start).
 */
import { useState } from 'react'
import { Info } from 'lucide-react'
import FileDropzone, { rejectedMessage } from '../../components/ui/FileDropzone'
import SvgIcon from '../../components/transit/SvgIcon'
import NewTransitionModal from '../../components/transit/NewTransitionModal'
import { useTransitSession } from '../../components/transit/session'
import { TRANSIT_ACCEPT, TRANSIT_ACCEPT_HINT, TRANSIT_VENDOR_QUESTIONNAIRE } from '../../constants'

const HOW_IT_WORKS = [
  { title: 'Upload', text: 'Bring what you have. Overmatch reads each document and sorts it by type.' },
  { title: 'Answer', text: "It drafts every section of the package from your documents, and asks when they don't say enough." },
  { title: 'Approve', text: 'Read and approve each section, with readiness levels computed from sourced facts, then generate the Word package.' },
]

export default function TransitHome() {
  const { user } = useTransitSession()
  const [dropped, setDropped] = useState<File[] | null>(null)
  const [rejected, setRejected] = useState<string | null>(null)
  const firstName = user.name?.trim().split(/\s+/)[0]

  return (
    <div className="flex justify-center px-6 pb-12 pt-[12vh]">
      <div className="flex w-full max-w-[625px] flex-col gap-8">
        <h1 className="text-2xl font-normal leading-7 text-foreground">
          <span className="block text-tertiary">{firstName ? `Welcome back, ${firstName}` : 'Welcome back'}</span>
          Where should we start?
        </h1>

        <div className="flex flex-col gap-6">
          <p className="text-base leading-[1.5] text-muted-foreground">
            Assess a technology&rsquo;s readiness for government adoption and produce a transition package for the
            program offices aligned to it: the case for carrying a proven capability from the office that developed
            it to the program that will field it.
          </p>
          <ol className="grid gap-4 sm:grid-cols-3">
            {HOW_IT_WORKS.map(({ title, text }, i) => (
              <li key={title} className="flex flex-col gap-1">
                <p className="text-sm font-medium leading-5 text-foreground">
                  <span className="text-tertiary">{i + 1}</span>&ensp;{title}
                </p>
                <p className="text-sm leading-5 text-muted-foreground">{text}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="flex flex-col gap-6">
          <div data-tour="home-required" className="flex flex-col gap-1 border-l border-primary pl-2 text-base">
            <p className="font-medium leading-[1.2] text-foreground">Required documents</p>
            <p className="leading-[1.5] text-muted-foreground">
              Capability statement, technical specifications, test and field reports, prior contracts or awards,
              government briefings
            </p>
            <p className="leading-[1.5] text-muted-foreground">
              We also require a completed vendor questionnaire.{' '}
              <a href={TRANSIT_VENDOR_QUESTIONNAIRE} download className="text-foreground underline underline-offset-2 hover:text-primary">
                Download the questionnaire
              </a>
            </p>
            {/* A mild alert: the attention yellow of the "Needs input" chips. */}
            <p role="note" className="mt-1 flex w-fit items-center gap-2 rounded-md bg-status-attention/60 px-3 py-1.5 text-sm leading-5 text-status-foreground dark:bg-status-attention">
              <Info className="h-4 w-4 shrink-0" aria-hidden />
              Please upload at least one document to get started.
            </p>
          </div>

          <div data-tour="home-upload">
          <FileDropzone
            accept={TRANSIT_ACCEPT}
            onFilesSelected={(files) => { setRejected(null); setDropped(files) }}
            onReject={(files) => setRejected(rejectedMessage(files))}
            className="group cursor-pointer rounded-lg border border-primary p-1"
          >
            <div className="flex flex-col items-center gap-2 rounded-[12px] bg-background px-3 py-6 transition-colors group-hover:bg-muted group-data-[drag-active=true]:bg-muted">
              <SvgIcon src="/transit/icon-files.svg" width={24} height={24} className="text-foreground" />
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <p className="text-base font-medium leading-6">Upload documents</p>
                <p className="text-xs leading-[1.2] tracking-[0.12px]">{TRANSIT_ACCEPT_HINT}</p>
              </div>
            </div>
          </FileDropzone>
          </div>
          {rejected && <p role="alert" className="-mt-4 text-sm text-destructive">{rejected}</p>}
        </div>
      </div>
      {dropped && <NewTransitionModal files={dropped} onClose={() => setDropped(null)} />}
    </div>
  )
}
